import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { useSlideIn } from '../hooks/useSlideIn';

export const Contact = () => {
  const anim = useSlideIn();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.privacy) {
      setError('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.detail || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } catch (err) {
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per Telefon.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Telefon',
      value: '0202 / 430 445 10',
      href: 'tel:020243044510',
    },
    {
      icon: Mail,
      label: 'E-Mail',
      value: 'info@fesch-elektrotechnik.de',
      href: 'mailto:info@fesch-elektrotechnik.de',
    },
    {
      icon: MapPin,
      label: 'Adresse',
      value: 'An der Bergbahn 2a, 42289 Wuppertal',
      href: 'https://maps.google.com/?q=An+der+Bergbahn+2a,+42289+Wuppertal',
    },
  ];

  const openingHours = [
    { day: 'Mo – Fr', time: '09:00 – 18:00' },
    { day: 'Samstag', time: '10:00 – 14:00' },
    { day: 'Sonntag', time: 'Geschlossen' },
  ];

  if (isSubmitted) {
    return (
      <section id="kontakt" data-testid="contact-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Vielen Dank für Ihre Anfrage!
            </h2>
            <p className="text-neutral-600 mb-8">
              Wir haben Ihre Nachricht erhalten und werden uns schnellstmöglich bei Ihnen melden.
            </p>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  subject: '',
                  message: '',
                  privacy: false,
                });
              }}
              className="btn-secondary"
            >
              Neue Anfrage senden
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <div ref={anim.ref} className={"max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 slide-in-left" + (anim.isVisible ? " visible" : "")}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">
            Kontakt
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
            Wir freuen uns auf Ihre Anfrage
          </h2>
          <p className="text-neutral-600 text-base lg:text-lg max-w-2xl mx-auto">
            Kontaktieren Sie uns für eine kostenlose und unverbindliche Beratung. Wir melden uns schnellstmöglich bei Ihnen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-8">
              Kontaktinformationen
            </h3>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              {contactInfo.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <a
                    key={index}
                    href={item.href}
                    target={item.icon === MapPin ? '_blank' : undefined}
                    rel={item.icon === MapPin ? 'noopener noreferrer' : undefined}
                    data-testid={`contact-${item.label.toLowerCase()}`}
                    className="flex items-start gap-4 p-4 bg-zinc-50 hover:bg-zinc-100 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-[#E31E24] flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-1">
                        {item.label}
                      </div>
                      <div className="text-neutral-900 font-medium group-hover:text-[#E31E24] transition-colors">
                        {item.value}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Opening Hours */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-[#E31E24]" />
                <h4 className="text-lg font-bold text-neutral-900">Öffnungszeiten</h4>
              </div>
              <div className="bg-zinc-50 p-6">
                {openingHours.map((item, index) => (
                  <div 
                    key={index}
                    className="flex justify-between py-2 border-b border-zinc-200 last:border-0"
                  >
                    <span className="font-medium text-neutral-700">{item.day}</span>
                    <span className={item.time === 'Geschlossen' ? 'text-neutral-400' : 'text-neutral-900'}>
                      {item.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Map Link */}
            <a
              href="https://maps.google.com/?q=An+der+Bergbahn+2a,+42289+Wuppertal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="block w-full bg-zinc-100 hover:bg-zinc-200 transition-colors p-8 text-center"
            >
              <MapPin className="w-8 h-8 text-[#E31E24] mx-auto mb-3" />
              <span className="font-bold text-neutral-900">Route berechnen</span>
              <p className="text-sm text-neutral-500 mt-1">Zu Google Maps wechseln</p>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-zinc-50 p-8 md:p-10">
            <h3 className="text-2xl font-bold text-neutral-900 mb-6">
              Nachricht senden
            </h3>
            
            <form onSubmit={handleSubmit} className="contact-form space-y-5">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="uppercase text-xs font-bold tracking-wider text-zinc-500 mb-2 block">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="contact-form-name"
                  className="rounded-none border-zinc-200 focus:ring-[#E31E24] focus:border-[#E31E24]"
                  placeholder="Ihr vollständiger Name"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="email" className="uppercase text-xs font-bold tracking-wider text-zinc-500 mb-2 block">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="contact-form-email"
                    className="rounded-none border-zinc-200 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="ihre@email.de"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="uppercase text-xs font-bold tracking-wider text-zinc-500 mb-2 block">
                    Telefon *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-form-phone"
                    className="rounded-none border-zinc-200 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="0202 123456"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject" className="uppercase text-xs font-bold tracking-wider text-zinc-500 mb-2 block">
                  Betreff *
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  data-testid="contact-form-subject"
                  className="rounded-none border-zinc-200 focus:ring-[#E31E24] focus:border-[#E31E24]"
                  placeholder="Worum geht es?"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="uppercase text-xs font-bold tracking-wider text-zinc-500 mb-2 block">
                  Nachricht *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="contact-form-message"
                  className="rounded-none border-zinc-200 focus:ring-[#E31E24] focus:border-[#E31E24] resize-none"
                  placeholder="Beschreiben Sie Ihr Anliegen..."
                />
              </div>

              {/* Privacy Checkbox */}
              <div className="flex items-start gap-3 relative z-10">
                <Checkbox
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, privacy: checked }))}
                  data-testid="contact-form-privacy"
                  className="mt-1 rounded-none border-zinc-300 data-[state=checked]:bg-[#E31E24] data-[state=checked]:border-[#E31E24] cursor-pointer"
                />
                <Label htmlFor="privacy" className="text-sm text-neutral-600 leading-relaxed cursor-pointer select-none">
                  Ich bin damit einverstanden, dass diese Daten zum Zweck der Kontaktaufnahme gespeichert und verarbeitet werden. 
                  Mir ist bekannt, dass ich meine Einwilligung jederzeit widerrufen kann. *
                </Label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-red-600 text-sm font-medium">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-form-submit"
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  'Nachricht senden'
                )}
              </button>

              <p className="text-xs text-neutral-500 text-center">
                * Pflichtfelder
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
